import React, { useState, useEffect } from 'react';
import { Row, Col, Spin, Table, Modal, DatePicker, Popconfirm, Card, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import * as API from 'utils/api';
import './CompanyProfile.scss';
import { notifyApiError, notifySuccess } from '../../utils/notification';

const { Text } = Typography;

const TimeTracker = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [timeEntries, setTimeEntries] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [timeEntryDetails, setTimeEntryDetails] = useState(null);

  useEffect(() => {
    fetchTimeEntries();
  }, []);

  const fetchTimeEntries = async () => {
    try {
      setIsLoading(true);
      const response = await API.getTimeEntries();
      if (response.success) {
        setTimeEntries(response.data || []);
      }
    } catch (error) {
      notifyApiError('Failed to load time entries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async record => {
    setEditingRecord(record);
    setStartTime(moment(record.start_time));
    setEndTime(record.end_time ? moment(record.end_time) : null);
    setEditModalVisible(true);

    try {
      const response = await API.getTimeEntry(record.id);
      if (response.success) {
        setTimeEntryDetails(response.data);
      }
    } catch (error) {
      notifyApiError('Failed to load time entry details');
    }
  };

  const handleDelete = async id => {
    try {
      const response = await API.deleteTimeEntry(id);
      if (response.success) {
        notifySuccess('Entry deleted');
        fetchTimeEntries();
      } else {
        notifyApiError(response.error || 'Failed to delete entry');
      }
    } catch (error) {
      notifyApiError('Failed to delete entry');
    }
  };

  const handleEditSubmit = async () => {
    if (!startTime) {
      notifyApiError('Please select start time');
      return;
    }

    try {
      const body = {
        start_time: startTime.toISOString(),
        end_time: endTime ? endTime.toISOString() : null,
      };
      const response = await API.updateTimeEntry(editingRecord.id, body);
      if (response.success) {
        setEditModalVisible(false);
        notifySuccess('Entry updated');
        fetchTimeEntries();
      } else {
        notifyApiError(response.error || 'Failed to update entry');
      }
    } catch (error) {
      notifyApiError('Failed to update entry');
    }
  };

  const renderActions = (_, record) => (
    <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
      <EditOutlined onClick={() => handleEdit(record)} />
      <Popconfirm title="Delete this entry?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
        <DeleteOutlined />
      </Popconfirm>
    </Row>
  );

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => `${record.user?.given_name || ''} ${record.user?.family_name || ''}`,
    },
    {
      title: 'Access Time',
      dataIndex: 'start_time',
      key: 'start_time',
      render: text => new Date(text).toLocaleString(),
    },
    {
      title: 'Leave Time',
      dataIndex: 'end_time',
      key: 'end_time',
      render: text => (text ? new Date(text).toLocaleString() : ''),
    },
    {
      title: 'Time Spent',
      key: 'time_spent',
      render: (_, record) => {
        if (!record.end_time) return '00:00:00';
        const start = new Date(record.start_time);
        const end = new Date(record.end_time);
        const hours = ((end - start) / (1000 * 60 * 60)).toFixed(2);
        return `${hours} hours`;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: renderActions,
    },
  ];

  return (
    <Row>
      <Col span={24}>
        <Spin tip="Loading..." spinning={isLoading}>
          <Row className="company_container" type="flex" justify="space-between">
            <Row type="flex" justify="start" align="middle">
              <Typography.Title level={2} className="company__title">
                Time Tracker
              </Typography.Title>
            </Row>
            <Table
              dataSource={timeEntries}
              columns={columns}
              rowKey="id"
              pagination={false}
              style={{ marginTop: '20px' }}
            />
          </Row>
        </Spin>

        <Modal
          title="Edit Time Entry"
          visible={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          onOk={handleEditSubmit}>
          {timeEntryDetails && (
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Row style={{ marginBottom: '8px' }}>
                <Col span={24}>
                  <Text strong>Employee: </Text>
                  <Text>
                    {timeEntryDetails.user?.given_name} {timeEntryDetails.user?.family_name}
                  </Text>
                </Col>
              </Row>
              {timeEntryDetails.report && (
                <>
                  <Row style={{ marginBottom: '8px' }}>
                    <Col span={24}>
                      <Text strong>Report ID #: </Text>
                      <Text>{timeEntryDetails.report.report_uuid}</Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Text strong>Report Name: </Text>
                      <Text>{timeEntryDetails.report.report_name}</Text>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          )}

          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Text>Start Time *</Text>
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                value={startTime}
                onChange={setStartTime}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <Text>End Time</Text>
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                value={endTime}
                onChange={setEndTime}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Modal>
      </Col>
    </Row>
  );
};

export default TimeTracker;
